/**
 * "Microsoft Contacts" source of the Select Recipients modal: read-only Outlook contacts from a
 * Microsoft 365 work/school account or a personal account (Outlook.com, Hotmail, Live).
 * Spec: Sendlinx_MS365_Contacts_Laravel_Developer_Specification.docx §9.
 *
 * - Disconnected: explanation + "Connect Microsoft Account" (Microsoft-hosted sign-in in a popup,
 *   so typed recipients, message and dropped files on the Send page are kept).
 * - Connected: account, last synced, Refresh, Disconnect; "My Outlook Contacts" with search,
 *   folder filter, sortable columns, select-all for the loaded rows and Load more.
 * - Rows feed the modal's single shared selection ({ email, name }); nothing is imported into
 *   Sendlinx. "Add to Contacts" in the footer stays the explicit way to save addresses.
 * - The Organization Directory (GAL) tab is not shown: it needs a separate permission review.
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Refresh from "@material-ui/icons/Refresh";
import PermContactCalendarOutlined from "@material-ui/icons/PermContactCalendarOutlined";
import LinkIcon from "@material-ui/icons/Link";
import Ms365Api, { openSignInPopup, runSignIn, closeSignInPopup, signInSameTab, MS365_RESULT_KEY, MS365_MESSAGE } from "../../../../assets/js/utils/contacts/ms365Api";

const PAGE = 50;
const lower = (v) => String(v || "").toLowerCase();
const initials = (c) => {
    const src = (c.name || c.email || "?").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
    return ((src[0] || "?")[0] + (src.length > 1 ? src[src.length - 1][0] : "")).toUpperCase();
};
const formatWhen = (iso) => {
    if(!iso){ return ""; }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const messageOf = (err, fallback) => (err && err.message && err.message !== "Request Cancelled" ? err.message : fallback);

function Microsoft365View({ classes, isSelected, toggle, toggleMany, already }){
    const [status, setStatus] = useState(null);           // null while loading
    const [statusError, setStatusError] = useState(null);
    const [busy, setBusy] = useState(false);               // connecting / disconnecting
    const [popupBlocked, setPopupBlocked] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [notice, setNotice] = useState(null);            // { kind: "ok"|"error"|"info", text }
    const [folders, setFolders] = useState([]);
    const [folder, setFolder] = useState("");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ key: "name", dir: 1 });
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({ total: 0, page: 0, hasMore: false, loading: false, truncated: false, syncedAt: null });
    const [listError, setListError] = useState(null);
    const requestId = useRef(0);
    const cancel = useRef(null);
    const mounted = useRef(true);
    const popupRef = useRef(null);          // the open sign-in popup, for Cancel
    const cancelled = useRef(false);
    const connected = !!(status && status.connected);

    const applyFailure = (err) => {
        const reason = err && err.reason;
        if(reason === "reconnect" || reason === "not_connected"){
            setStatus(s => ({ ...(s || {}), configured: true, connected: false, status: reason === "reconnect" ? "reconnect" : "disconnected" }));
            setRows(null);
            return true;
        }
        return false;
    };

    const loadStatus = () => {
        setStatusError(null);
        return Ms365Api.status().then(data => {
            if(mounted.current){ setStatus(data); }
            return data;
        }).catch(err => {
            if(mounted.current){ setStatusError(messageOf(err, "Your Microsoft account status could not be loaded.")); setStatus(s => s || { configured: true, connected: false, status: "disconnected" }); }
            return null;
        });
    };

    const loadContacts = (page = 1, refresh = false) => {
        const id = ++requestId.current;
        if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        cancel.current = source;
        setListError(null);
        setMeta(m => ({ ...m, loading: true }));
        return Ms365Api.contacts({ q: search.trim(), folder, sort: sort.key, dir: sort.dir === 1 ? "asc" : "desc", page, limit: PAGE, refresh: refresh ? 1 : "" }, source).then(data => {
            if(id !== requestId.current || !mounted.current){ return; }
            const items = data.items || [];
            setRows(prev => (page === 1 || !prev ? items : prev.concat(items.filter(r => !prev.some(p => p.id === r.id)))));
            setMeta({ total: data.total || 0, page, hasMore: !!data.hasMore, loading: false, truncated: !!data.truncated, syncedAt: data.syncedAt || null });
            if(refresh || page === 1){ setStatus(s => (s ? { ...s, lastSyncedAt: data.syncedAt || s.lastSyncedAt } : s)); }
        }).catch(err => {
            if(id !== requestId.current || !mounted.current || (err && err.message === "Request Cancelled")){ return; }
            setMeta(m => ({ ...m, loading: false }));
            if(!applyFailure(err)){
                setRows(prev => prev || []);
                // Microsoft's error code (never its message) helps support find the cause.
                setListError(messageOf(err, "Your Microsoft contacts could not be loaded.") + (err && err.graphCode ? " (Microsoft error: "+err.graphCode+")" : ""));
            }
        });
    };

    const loadFolders = () => Ms365Api.folders().then(data => {
        if(mounted.current){ setFolders(data.folders || []); }
    }).catch(err => { applyFailure(err); });

    useEffect(() => {
        mounted.current = true;
        loadStatus();
        // A sign-in finished in another window (or after the popup lost its opener): refresh.
        const onResult = (payload) => {
            if(!payload || !mounted.current){ return; }
            if(payload.ok){ setNotice({ kind: "ok", text: "Microsoft account connected." }); setPopupBlocked(false); }
            else{ setNotice({ kind: "error", text: payload.message || "Your Microsoft account was not connected." }); }
            closeSignInPopup(popupRef.current);
            setBusy(false);
            loadStatus();
        };
        const onStorage = (e) => { if(e.key === MS365_RESULT_KEY && e.newValue){ try{ onResult(JSON.parse(e.newValue)); }catch(x){ } } };
        const onMessage = (e) => { if(e.origin === window.location.origin && e.data && e.data.type === MS365_MESSAGE){ onResult(e.data); } };
        window.addEventListener("storage", onStorage);
        window.addEventListener("message", onMessage);
        // Same-tab fallback: the callback page left its result here.
        try{
            const raw = sessionStorage.getItem(MS365_RESULT_KEY);
            if(raw){
                sessionStorage.removeItem(MS365_RESULT_KEY);
                const r = JSON.parse(raw);
                setNotice(r.ok ? { kind: "ok", text: "Microsoft account connected." } : { kind: "error", text: r.message || "Your Microsoft account was not connected." });
            }
        }catch(e){ }
        return () => {
            mounted.current = false;
            closeSignInPopup(popupRef.current);
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("message", onMessage);
            if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(connected){ loadFolders(); } else { setFolders([]); setRows(null); }
    }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

    // Search is debounced; folder and sort changes reload at once.
    useEffect(() => {
        if(!connected){ return undefined; }
        const timer = setTimeout(() => loadContacts(1), rows === null ? 0 : 300);
        return () => clearTimeout(timer);
    }, [connected, search, folder, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    const connect = () => {
        setNotice(null);
        setPopupBlocked(false);
        const popup = openSignInPopup();          // synchronously, inside the click
        if(!popup){ setPopupBlocked(true); return; }
        popupRef.current = popup;
        cancelled.current = false;
        setBusy(true);
        runSignIn(popup).then(result => {
            popupRef.current = null;
            if(!mounted.current){ return; }
            setBusy(false);
            if(result && result.closed && cancelled.current){
                setNotice({ kind: "info", text: "Microsoft sign-in cancelled. Nothing was connected." });
                loadStatus();
                return;
            }
            if(result && result.timedOut){
                setNotice({ kind: "info", text: "The Microsoft sign-in took too long and was closed. Please connect again." });
                loadStatus();
                return;
            }
            if(result && result.ok){
                setNotice({ kind: "ok", text: "Microsoft account connected." });
                if(result.status){ setStatus(result.status); } else { loadStatus(); }
            }else if(result && result.closed){
                loadStatus().then(s => {
                    if(mounted.current && !(s && s.connected)){
                        setNotice({ kind: "info", text: "The Microsoft sign-in window was closed before it finished. If you completed it, this view updates automatically." });
                    }
                });
            }else{
                setNotice({ kind: "error", text: (result && result.message) || "Your Microsoft account was not connected." });
                loadStatus();
            }
        }).catch(err => {
            popupRef.current = null;
            if(!mounted.current){ return; }
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Microsoft sign-in could not be started.") });
        });
    };
    // Microsoft pages such as "Need admin approval" don't return to Sendlinx on their own.
    const cancelSignIn = () => {
        cancelled.current = true;
        closeSignInPopup(popupRef.current);
    };
    const continueInThisTab = () => {
        setBusy(true);
        signInSameTab().catch(err => {
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Microsoft sign-in could not be started.") });
        });
    };
    const disconnect = () => {
        setBusy(true);
        Ms365Api.disconnect().then(data => {
            if(!mounted.current){ return; }
            setBusy(false);
            setConfirmDisconnect(false);
            setStatus(data);
            setSearch("");
            setFolder("");
            setNotice({ kind: "ok", text: "Microsoft account disconnected. Sendlinx deleted its stored access. Recipients you already selected stay selected." });
        }).catch(err => {
            if(!mounted.current){ return; }
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Your Microsoft account could not be disconnected.") });
        });
    };

    const sortHeader = (key, label) => (
        <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button type="button" className={classes.sortButton} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
                {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
            </button>
        </th>
    );

    const header = (
        <div className={classes.msHeader}>
            <span className={classes.msIcon} aria-hidden="true"><PermContactCalendarOutlined /></span>
            <div>
                <h3 className={classes.msTitle}>Microsoft Contacts</h3>
                <p className={classes.muted}>Microsoft 365, Outlook, Hotmail &amp; Live</p>
            </div>
        </div>
    );
    const noticeBox = notice ? (
        <div className={notice.kind === "error" ? classes.error : notice.kind === "ok" ? classes.success : classes.notice} role={notice.kind === "error" ? "alert" : "status"}>{notice.text}</div>
    ) : null;

    if(status === null){
        return <>{header}<p className={classes.empty}>Checking your Microsoft account connection…</p></>;
    }

    if(!connected){
        const reconnect = status.status === "reconnect";
        const notConfigured = status.configured === false;
        return (<>
            {header}
            {noticeBox}
            {statusError ? <div className={classes.error} role="alert">{statusError}</div> : null}
            <div className={classes.msPanel}>
                {notConfigured ? <>
                    <p><strong>Microsoft contacts aren't available yet.</strong></p>
                    <p className={classes.muted}>Your Sendlinx administrator needs to finish the Microsoft setup. You can still type addresses or use your Sendlinx contacts.</p>
                </> : <>
                    <h4 className={classes.msConnectTitle}>
                        <PermContactCalendarOutlined aria-hidden="true" />{reconnect ? "Reconnect your Microsoft account" : "Connect your Microsoft account"}
                    </h4>
                    {reconnect ?
                        <p className={classes.msWarn} role="status"><ErrorOutline aria-hidden="true" />Your Microsoft account connection{status.account && status.account.email ? " ("+status.account.email+")" : ""} expired or was revoked. Reconnect to keep using your contacts.</p>
                    : <p className={classes.msLead}>Access your Outlook, Hotmail, Live or Microsoft 365 contacts securely.</p>}
                    <button type="button" className={classes.msConnectButton} onClick={connect} disabled={busy}>
                        <LinkIcon aria-hidden="true" />
                        {busy ? "Waiting for Microsoft sign-in…" : reconnect ? "Reconnect Microsoft Account" : "Connect Microsoft Account"}
                    </button>
                    {busy && popupRef.current ?
                        <div className={classes.msWaiting} role="status">
                            <span>Finish signing in in the Microsoft window. If Microsoft says your organization's admin must approve Sendlinx, that window can't continue; close it or cancel here.</span>
                            <button type="button" className={classes.secondary} onClick={cancelSignIn}>Cancel sign-in</button>
                        </div>
                    : null}
                    <ul className={classes.msList}>
                        <li>You sign in on Microsoft's own page. Sendlinx never sees your Microsoft password.</li>
                        <li>Sendlinx only asks to read your contacts. Nothing is imported into Sendlinx or shared.</li>
                        <li>For a work or school account, your IT administrator may need to approve Sendlinx first.</li>
                    </ul>
                    {popupBlocked ?
                        <div className={classes.notice} role="alert">
                            Your browser blocked the Microsoft sign-in window. Allow pop-ups for this site and try again, or
                            {" "}<button type="button" className={classes.inlineLink} onClick={continueInThisTab} disabled={busy}>sign in in this tab</button>.
                            {" "}Signing in in this tab reloads the Send page, so typed recipients, the message and selected files are cleared.
                        </div>
                    : null}
                </>}
            </div>
        </>);
    }

    const list = rows || [];
    const allShown = list.length > 0 && list.every(r => isSelected(r.email));
    const filtered = search.trim() !== "" || folder !== "";
    const synced = formatWhen(meta.syncedAt || status.lastSyncedAt);
    return (<>
        {header}
        {noticeBox}
        <div className={classes.msConnected}>
            <CheckCircle className={classes.msOk} aria-hidden="true" />
            <span className={classes.msAccount}>Connected to <strong>{(status.account && (status.account.email || status.account.name)) || "your Microsoft account"}</strong>
                <span className={classes.subLine}>{status.account && status.account.type === "personal" ? "Personal Microsoft account" : status.account && status.account.type === "work" ? "Work or school account" : ""}{status.account && status.account.type && synced ? " · " : ""}{synced ? "Last synced: "+synced : ""}</span></span>
            <button type="button" className={classes.linkButton} onClick={() => { loadFolders(); loadContacts(1, true); }} disabled={meta.loading || busy}
                aria-label="Refresh Microsoft contacts"><Refresh aria-hidden="true" />Refresh</button>
            {confirmDisconnect ? <span className={classes.msConfirm} role="group" aria-label="Confirm disconnect">
                <span>Disconnect this account?</span>
                <button type="button" className={classes.danger} onClick={disconnect} disabled={busy}>{busy ? "Disconnecting…" : "Disconnect"}</button>
                <button type="button" className={classes.secondary} onClick={() => setConfirmDisconnect(false)} disabled={busy}>Keep</button>
            </span> :
            <button type="button" className={classes.msDisconnect} onClick={() => setConfirmDisconnect(true)}>Disconnect</button>}
        </div>
        {confirmDisconnect ?
            <p className={classes.hint}>Sendlinx deletes its stored access to this account. To remove the app's permission in Microsoft as well, open <a className={classes.inlineLink} href="https://myapps.microsoft.com" target="_blank" rel="noopener noreferrer">My Apps</a>.</p>
        : null}
        <div className={classes.tabs} role="tablist" aria-label="Microsoft contact sources">
            <button type="button" role="tab" aria-selected="true" className={classes.tab+" "+classes.tabActive}>My Outlook Contacts</button>
        </div>
        <div className={classes.toolbar}>
            <input type="search" className={classes.search} placeholder="Search contacts by name, email or company…" aria-label="Search Microsoft contacts"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className={classes.select} aria-label="Contact folder" value={folder} onChange={(e) => setFolder(e.target.value)}>
                <option value="">All Folders</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
        </div>
        {listError ? <div className={classes.error} role="alert">{listError} <button type="button" className={classes.inlineLink} onClick={() => loadContacts(1)}>Try again</button></div> : null}
        <div className={classes.tableWrap} aria-busy={meta.loading}>
            <table className={classes.table}>
                <thead><tr>
                    <th scope="col" style={{ width: 44 }}>
                        <input type="checkbox" className={classes.checkbox} aria-label={"Select all "+list.length+" Microsoft contacts shown"}
                            checked={allShown} disabled={list.length === 0} onChange={(e) => toggleMany(list, e.target.checked)} />
                    </th>
                    {sortHeader("name", "Name")}{sortHeader("email", "Email")}{sortHeader("company", "Company")}
                </tr></thead>
                <tbody>
                    {rows === null ? <tr><td colSpan={4} className={classes.empty}>Loading your Microsoft contacts…</td></tr> :
                        list.length === 0 ? <tr><td colSpan={4} className={classes.empty}>{filtered ? "No Microsoft contacts match." : "No contacts with an email address were found in your Microsoft account."}</td></tr> :
                        list.map(r => (
                            <tr key={r.id} className={isSelected(r.email) ? classes.rowSelected : ""}>
                                <td><input type="checkbox" className={classes.checkbox} checked={isSelected(r.email)} onChange={() => toggle(r)}
                                    aria-label={"Select "+(r.name ? r.name+" ("+r.email+")" : r.email)} /></td>
                                <td>
                                    <span className={classes.nameCell}>
                                        <span className={classes.avatar} aria-hidden="true">{initials(r)}</span>
                                        <span>{r.name || <span className={classes.muted}>—</span>}{already.has(lower(r.email)) ? <span className={classes.muted}> (added)</span> : null}
                                            {r.folder && folder === "" && r.folder !== "Contacts" ? <span className={classes.subLine}>{r.folder}</span> : null}</span>
                                    </span>
                                </td>
                                <td>{r.email}
                                    {r.emailCount > 1 ? <span className={classes.subLine}>Address {r.emailIndex} of {r.emailCount}</span> : null}</td>
                                <td>{r.company || <span className={classes.muted}>—</span>}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
        {rows !== null && list.length > 0 ?
            <div className={classes.toolbar}>
                <span className={classes.muted}>
                    Showing {list.length} of {meta.total}{filtered ? " matching" : ""}.
                    {allShown && meta.hasMore ? " All shown are selected; load more to select the rest." : " Select all applies to the rows shown."}
                    {meta.truncated ? " Only the first 5,000 contacts are searched." : ""}
                </span>
                {meta.hasMore ?
                    <button type="button" className={classes.linkButton} disabled={meta.loading} onClick={() => loadContacts(meta.page + 1)}>
                        {meta.loading ? "Loading…" : "Load more"}
                    </button>
                : null}
            </div>
        : null}
    </>);
}

Microsoft365View.propTypes = {
    classes: PropTypes.object.isRequired,
    isSelected: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    toggleMany: PropTypes.func.isRequired,
    already: PropTypes.object.isRequired
};

export default Microsoft365View;
